import { asId, type Message, type MessageId, type Reaction, type Seq, type UserRef } from '@cyphex/types';
import type { OutboxItem } from '../types';

/**
 * The pill row the user should see the instant they click, before the server
 * answers. Mirrors the server's own recompute so the optimistic and the
 * authoritative rows agree.
 */
export function optimisticReactions(current: readonly Reaction[], emoji: string, reacted: boolean): Reaction[] {
  if (reacted) {
    return current
      .map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r))
      .filter((r) => r.count > 0);
  }
  if (!current.some((r) => r.emoji === emoji)) {
    return [...current, { emoji, count: 1, reacted: true }];
  }
  return current.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, reacted: true } : r));
}

/**
 * Renders a queued send as a real bubble immediately. The message id IS the
 * `clientMsgId`, so when the server echo arrives (REST response or the
 * `message.new` frame) the row is replaced rather than duplicated.
 */
export function optimisticMessage(item: OutboxItem, sender: UserRef, seq: Seq): Message {
  const createdAt = new Date(item.createdAt).toISOString();
  return {
    id: asId<MessageId>(item.clientMsgId),
    conversationId: item.conversationId,
    seq,
    kind: item.voice ? 'voice' : 'text',
    state: item.scheduledFor ? 'scheduled' : 'sent',
    sender,
    appId: null,
    subject: null,
    body: item.body,
    bodyText: item.text,
    threadRootId: item.threadRootId,
    replyToId: item.replyToId,
    replyTo: null,
    replyCount: 0,
    lastReplyAt: null,
    replyParticipants: [],
    forwardedFrom: null,
    mentions: item.mentions,
    attachments: [],
    previews: [],
    card: null,
    poll: null,
    meeting: null,
    voice: item.voice ? { ...item.voice, url: '' } : null,
    systemEvent: null,
    reactions: [],
    isPinned: false,
    isImportant: false,
    isUrgent: false,
    isBookmarked: false,
    isSaved: false,
    reminderAt: null,
    editedAt: null,
    editCount: 0,
    scheduledFor: item.scheduledFor,
    expiresAt: null,
    createdAt,
    updatedAt: createdAt,
    deletedAt: null,
  };
}

/** Exponential backoff with jitter, capped — never hammers a struggling API. */
export function nextBackoff(attempts: number, baseMs: number): number {
  const capped = Math.min(30_000, baseMs * 2 ** Math.min(attempts, 6));
  return Date.now() + capped + Math.random() * 250;
}
